import {
  AfterContentInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import {FormArray, FormBuilder, FormGroup, Validators, FormControl} from '@angular/forms';
import {
  debounceTime,
  distinctUntilChanged,
  finalize,
  pairwise,
  takeWhile,
  throttleTime
} from 'rxjs/operators';
import { Subscription, Subject, of } from 'rxjs';
import {ResidentialService } from '../../core/services/residential.service';
import {stateAbbreviations} from '../../core/constant';
import {BrandingService} from '../../core/services/branding.service';
import {AuthService} from '../../core/services';
import {ToastrService} from 'ngx-toastr';
import {PolicyInsurable} from '../../core/models/address.model';
import { Insurable } from '../../core/models/insurable.model';
import * as _ from 'lodash';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {ResidentialLeadService} from '../residential-lead.service';
import {FormsService} from '../../shared/forms.service';
import {TranslateService} from '@ngx-translate/core';
import { ActivatedRoute, Params } from '@angular/router';
import {DataLayerResidentialService} from '../../core/services/data-layer-residential.service';
import {ResidentialIconService} from '../residential-icon.service';
import {QuestionsPopupComponent} from '../../shared/questions-popup/questions-popup.component';
import {MatDialog} from '@angular/material/dialog';
import {CUSTOM_LIMITS} from '../custom-limits';
import {PmAccount} from '../../core/dto/pm-account';
import {isAddressFilled} from '../../ui/pm-account/pm-account.component';
import {PmDetailsFormComponent} from '../pm-details-form/pm-details-form.component';
import {Unit} from '../../core/models/unit.model';
import {cloneDeep} from 'lodash';
import {animate, style, transition, trigger} from '@angular/animations';
import { EmailValidateService } from '../../core/services/email-validate.service';
import * as moment from 'moment';
import { ObjectIdValidator } from '../../auth/_helpers/object-id.validator';
import { customEmailValidator } from '../../core/utils/custom-email-validator.utils';
import { UserMatchService } from '../../auth/user-match.service';
import { BrandingInfo } from '../../core/models/branding.model';
import { PaperlessAgreementComponent } from './paperless-agreement/paperless-agreement.component';
import { prohibitedCharactersValidator } from 'src/app/core/utils/validators/prohibited-characters.valdator';
import { LeaseRestrictedRes, PmaService } from '../../pma/pma.service';

export interface ResState {
  id: number;
  number_insured: number;
  billing_period: string;
  deductible: number;
  agency_id?: number;
  hurricane?: number;
  effective_date?: any;
  coverage_selection?: any[];
}

export interface QuestionI {
  options: string[];
  questionId: string;
  title: string;
  value: string;
}

export interface GoogleAddressI {
  street_number: string;
  route: string;
  locality: string;
  administrative_area_level_1: string;
  administrative_area_level_3: string;
  postal_code: string;
  neighborhood: string;
  sublocality_level_1: string;
}

export interface CommunityUnit {
  id: number;
  title: string;
  insurable_id: number;
}

const MAX_ADDITIONAL_INSURED = 4;

@UntilDestroy()
@Component({
  selector: 'app-residential-form',
  templateUrl: './residential-form.component.html',
  styleUrls: ['./residential-form.component.scss'],
  animations: [
    trigger('fade', [
      transition('void => *', [
        style({opacity: 0}),
        animate(600, style({opacity: 1}))
      ])
    ])
  ]
})
export class ResidentialFormComponent implements OnInit, OnDestroy, AfterContentInit {
  @Input() residential;
  @Input() loader = false;
  @Input() policyApplicationId;
  initialLeadDataEntered = false;
  loadingCoverageOptions = false;

  _live = true;
  @Input() set live(live) {
    this._live = live;
    if (live) {
      this.changeDetectorRef.reattach();
    } else {
      this.changeDetectorRef.detach();
    }
  }

  get live() {
    return this._live;
  }

  @Output() formSubmit = new EventEmitter();
  @Output() totalChange = new EventEmitter<number>();
  @Output() periodChange = new EventEmitter();
  residentialForm: FormGroup;
  agencyId = 1;
  accountId = null;
  submitted = false;
  customRgPage$ = this.brandingService.customRgPage$;
  billingStrategyId = [];
  billingStrategy: any[];
  state: ResState = {
    id: null,
    number_insured: 0,
    billing_period: 'monthly',
    deductible: 250
  };
  unitSearchResult: PolicyInsurable[] = [];
  filteredUnits$;
  policyRatesAttributes = [];
  minDate;
  maxDate = null;
  installmentDays = Array.from({length: 28}, (v, i) => i + 1);
  checkboxesIds = [];
  stateList = stateAbbreviations;
  counties;
  optionalAddons$;
  additionalOptions = [];
  adjustedNew = false;
  coverageOptions;
  maxBirthdayDate;
  initialLoading = false;
  unitLoading = false;
  addressResults = [];
  newCarrierFormInited = new Subject();
  questions: QuestionI[] = [] as QuestionI[];
  answers = {};
  coverageOptionsError;
  showCountyField = false;
  communityUnits: CommunityUnit[] = [];
  filteredCommunityUnits: CommunityUnit[] = [];
  controlConfirmToday = new FormControl(false);
  controlAllTenantsListed = new FormControl(false);
  preferred_ho4;
  fetchedCommunity;
  /**
   * Determinate if is the first load of selectAddress function.
   */
  firstLoad = true;

  private lastChangedField;
  @ViewChild('addressField', {static: false}) addressField: ElementRef;
  @ViewChild('confirm') confirmElem: ElementRef;
  addressWasSelected = false;
  addressError = false;
  @Output() coverageOptionsInited = new EventEmitter();
  @ViewChild(PmDetailsFormComponent, {static: false}) pmDetailsFormComponent: PmDetailsFormComponent;
  subscription = new Subscription();

  maxAdditionalInsured = MAX_ADDITIONAL_INSURED;
  nonpreferredAccountSelection = false;
  nonpreferredAddressSelection = false;
  pmaSingleHouses = false;
  nonApplicableControl = new FormControl(false);
  isBrandingSecondNature = false;
  primaryAddress;
  brandingSnapshot: BrandingInfo;

  /**
   * Is component not destroyed
   */
  isActive = true;

  constructor(
    private fb: FormBuilder,
    private resService: ResidentialService,
    private brandingService: BrandingService,
    private authService: AuthService,
    private formsService: FormsService,
    private toastr: ToastrService,
    public changeDetectorRef: ChangeDetectorRef,
    private residentialLeadService: ResidentialLeadService,
    private residentialIconService: ResidentialIconService,
    private translate: TranslateService,
    private activatedRoute: ActivatedRoute,
    private dataLayerResidentialService: DataLayerResidentialService,
    private dialog: MatDialog,
    private emailValidateService: EmailValidateService,
    private userMatchService: UserMatchService,
    private pmaService: PmaService
  ) {
    this.residentialForm = this.fb.group({
      address: ['', []],
      community: ['', []],
      building: ['', []],
      building_address: ['', []],
      building_address_checkbox: ['', []],
      unit: ['', []],
      effective_date: ['', [Validators.required]],
      expiration_date: [{value: '', disabled: true }, [Validators.required]],
      billing_strategy_id: ['', [Validators.required]],
      first_name: ['', [Validators.required, Validators.maxLength(30), prohibitedCharactersValidator()]],
      last_name: ['', [Validators.required, Validators.maxLength(30), prohibitedCharactersValidator()]],
      middle_name: ['', [Validators.maxLength(25), prohibitedCharactersValidator()]],
      birth_date_main: ['', [Validators.required]],
      email: ['', [Validators.required, customEmailValidator()]],
      contact_phone: ['', [Validators.required]],
      street_name: ['', [Validators.required]],
      street_two: ['', []],
      city: ['', []],
      state: ['', []],
      postal_code: ['', []],
      /*      register_account: new FormControl( true),*/
      insured_user_arr: this.fb.array([
        fb.group({
          spouse: [false, []],
          first_name: [null, [Validators.maxLength(30), prohibitedCharactersValidator()]],
          last_name: [null, [Validators.maxLength(30), prohibitedCharactersValidator()]],
          middle_name: [null, [Validators.maxLength(25), prohibitedCharactersValidator()]],
          contact_phone: [null, []],
          birth_date_sub: [null, []],
          email: [null, [customEmailValidator()]]
        })
      ]),
      community_details: this.fb.group({
        number_of_units: '',
        gated: '',
        year_built: ['', [Validators.min(1900), Validators.max(new Date().getFullYear())]],
        years_professionally_managed: ''
      }),
      installment_day: [1, [Validators.required]],
      /* PM Details fields disabled by default (this is being enabled by the child component) */
      pm_details: this.fb.group({
        entity_type: ['company'],
        company_name: [''],
        email_address: [''],
        phone_number: [''],
        first_name: [''],
        last_name: [''],
        address: [''],
        address2: [''],
        city: [''],
        state: [''],
        postal_code: ['']
      }),
      pm_account: [],
      neighborhood: [],
      county: [],
      insurable_id: [],
      paperless_flag: [true]
    });

    const tomorrow = moment().add(1, 'days');
    const expiration = moment(tomorrow).add(1, 'years');

    this.residentialForm.get('effective_date').setValue(tomorrow.toDate());
    this.residentialForm.get('expiration_date').setValue(expiration.toDate());

    this.agencyId = this.brandingService.getBrandingInfoSnapshot().agency_id;
    this.accountId = this.brandingService.getBrandingInfoSnapshot().account_id || null;

    this.minDate = moment(moment()).add(0, 'day').toDate();
    this.maxBirthdayDate = moment(moment.now()).subtract(18, 'year').toDate();
    this.onChanges();
    /*    this.registerAccountNotice$ = brandingService.registerAccountNotice$;*/
    this.authService.user.pipe(
      untilDestroyed(this)
    ).subscribe(user => {
      if (!user) {
        return;
      }
      this.residentialForm.patchValue({
        first_name: user.profile_attributes.first_name,
        last_name: user.profile_attributes.last_name,
        birth_date_main: user.profile_attributes.birth_date,
        email: user.email,
        contact_phone: user.profile_attributes.contact_phone
      });
    });

    this.residentialForm.valueChanges.pipe(
      untilDestroyed(this)
    ).subscribe((v) => {
      this.checkEmailsDifference();
    });
    this.optionalAddons$ = brandingService.optionalAddons$;

    this.residentialForm.valueChanges.pipe(
      throttleTime(200),
      distinctUntilChanged(),
      untilDestroyed(this)
    ).subscribe(formValue => {
      const valuesToSave = cloneDeep(formValue);
      delete valuesToSave.billing_strategy_id;
      const RENTERS_LOCAL_STORAGE_KEY = ResidentialService.RENTERS_LOCAL_STORAGE_KEY;
      localStorage.setItem(RENTERS_LOCAL_STORAGE_KEY, JSON.stringify(valuesToSave));
    });

  }

  ngOnInit(): void {
    const brandingProfileAttributes = this.brandingService.getBrandingInfoSnapshot().profile_attributes;
    this.nonpreferredAccountSelection = brandingProfileAttributes.some(item => item.name === 'nonpreferred_account_selection' && item.value);
    this.nonpreferredAddressSelection = brandingProfileAttributes.some(item => item.name === 'nonpreferred_address_selection' && item.value);
    this.pmaSingleHouses = brandingProfileAttributes.some(item => item.name === 'pma_single_houses_mode' && item.value);
    if (this.pmaSingleHouses) {
      this.nonpreferredAddressSelection = true;
    }

    if (this.nonpreferredAccountSelection) {
      this.initPMAccountSelector();
    }

    this.onUnitChanges();
    this.userMatchService.onChangesUserInfo(this.residentialForm);
    this.brandingSnapshot = this.brandingService.getBrandingInfoSnapshot();
  }

  ngAfterContentInit(): void {
    const sub = this.activatedRoute.queryParams.subscribe((queryParams: Params) => this.initFormData(queryParams));
    this.subscription.add(sub);
  }

  // This method is for PMC redirect data init
  initFormData(queryParams: Params): void {
    // if the first name is present in the queryParams, that means data mapping mode is there
    if (queryParams.first_name) {
      this.residentialForm.patchValue(queryParams);
    }
  }

  setAccountId(accountId) {
    this.accountId = accountId;
  }

  isNotEmptyUser(user) {
    const found = Object.keys(user).find(key => user[key]);
    return !!found;
  }

  ngOnDestroy(): void {
    this.isActive = false;
    this.subscription.unsubscribe();
  }

  selectCounty(county) {
    this.resService.postCounty(this.getInsurableId, county).subscribe(
      res => console.log(res, 'success set qbe county'),
      err => console.log(err, ' error posting qbe county'));
  }

  setCounty(googleAddress) {
    const foundCounty = googleAddress.address_components.find(ac => (ac.types || []).includes('administrative_area_level_2'));
    const foundNeighborhood = googleAddress.address_components.find(ac => (ac.types || []).includes('neighborhood'));

    const county = foundCounty && foundCounty.long_name ? foundCounty.long_name : '';
    const neighborhood = foundNeighborhood && foundNeighborhood.long_name ? foundNeighborhood.long_name : '';
    this.residentialForm.get('county').patchValue(county);
    this.residentialForm.get('neighborhood').patchValue(neighborhood);

    localStorage.setItem('RENTERS_COUNTY', county);
    localStorage.setItem('RENTERS_NEIGHBORHOOD', neighborhood);
  }

  clearCommunityDetailsValidators() {
    this.residentialForm.controls['community_details'].get('number_of_units').clearValidators();
    this.residentialForm.controls['community_details'].get('gated').clearValidators();
    this.residentialForm.controls['community_details'].get('year_built').setValidators(
      [Validators.min(1900), Validators.max(new Date().getFullYear())]
    );
    this.residentialForm.controls['community_details'].get('years_professionally_managed').clearValidators();
  }

  setCommunityDetailsValidators() {
    this.residentialForm.controls['community_details'].get('number_of_units').setValidators([Validators.required]);
    this.residentialForm.controls['community_details'].get('gated').setValidators([Validators.required]);
    this.residentialForm.controls['community_details'].get('year_built').setValidators(
      [Validators.min(1900), Validators.max(new Date().getFullYear()), Validators.required]
    );
    this.residentialForm.controls['community_details'].get('years_professionally_managed').setValidators([Validators.required]);
  }

  createResidentialLead(lastChangedField) {
    this.lastChangedField = lastChangedField;
    if (this.lastChangedField && this.residentialForm.controls['email'].valid) {
      const changedSection = this.residentialLeadService.getLeadSectionByFieldName(this.lastChangedField);
      const leadData = this.residentialLeadService.convertFormDataToLead(this.residentialForm.getRawValue());
      leadData.lead_event_attributes.data.last_visited_page = changedSection;
      if (this.residentialForm.get('insurable_id') && this.residentialForm.get('insurable_id').value) {
        leadData.lead_event_attributes.data.insurable_id = this.residentialForm.get('insurable_id').value;
      }
      this.residentialLeadService.createLead(leadData);
    }
  }

  onChanges() {
    const billingPeriod = this.residentialForm.get('billing_strategy_id');
    const insuredUserArr = this.residentialForm.get('insured_user_arr') as FormArray;
    billingPeriod.valueChanges
      .pipe(
        distinctUntilChanged(),
        untilDestroyed(this)
      ).subscribe(res => {
      this.state.billing_period = (res && this.billingStrategy) ? this.billingStrategy.find(str => str.title === res).id : res;
      this.periodChange.emit(res);
      if (res) {
        this.getCoverageLimits();
      }
    });
    insuredUserArr.valueChanges.pipe(
      pairwise(),
      distinctUntilChanged(),
      untilDestroyed(this)
    ).subscribe(([prev, cur]) => {
      if (prev.length !== cur.length) {
        this.state.number_insured = cur.length;
        this.getCoverageLimits();
      }
    });
  }

  onUnitChanges() {
    this.subscription.add(
      this.residentialForm.get('unit').valueChanges.pipe(
        distinctUntilChanged(),
        debounceTime(800),
      ).subscribe(unit => {
        if (unit) {
          this.dataLayerResidentialService.formImpressionOne();
        }
      })
    );
  }

  handleInitForm(event) {
    event.stopPropagation();
    event.preventDefault();
    const unit = this.residentialForm.get('unit').value;
    this.residentialForm.get('unit').patchValue(unit);
  }

  get isValidInitForm() {
    return this.residentialForm.get('first_name').value && this.residentialForm.get('last_name').value &&
      this.residentialForm.get('email').value && this.residentialForm.get('email').valid &&
      (this.residentialForm.get('unit').valid || this.residentialForm.get('unit').disabled) &&
      this.primaryAddress && this.primaryAddress.street_name
  }

  initForm() {
    if (this.pmaSingleHouses) {
      this.onPmaSingleHouses()
    } else {
      const unit = this.residentialForm.get('unit').value;
      this.residentialForm.get('unit').patchValue(unit);
    }
  }

  onPmaSingleHouses() {
    const address = this.residentialForm.get('address').value;
    this.residentialForm.get('address').patchValue(address);
  }

  setAddressAttributes(addressAttributes) {
    this.primaryAddress = addressAttributes;
    if (this.primaryAddress) {
      this.createNewResindetialPolicyApplication();
    }
  }

  stopInitialLoading() {
    this.initialLoading = false;
  }

  setPreferredho4(preferred_ho4) {
    this.preferred_ho4 = preferred_ho4;
  }

  createNewResindetialPolicyApplication() {
    this.initialLoading = true;

    const authTokenInfo = this.pmaService.insurableByAuthTokenStore.getValue();
    let userId: number;

    if (authTokenInfo && authTokenInfo?.user?.id) {
      userId = Number(authTokenInfo?.user?.id);
    }

    const communityIds = this.brandingService.getBrandingInfoSnapshot().profile_attributes
      .find(attr => attr.name === 'lease_restriction_community_ids')?.value || false;

    if (this.brandingSnapshot?.lease_restricted_user_matching === true
      && (
        !communityIds ||
        (
          communityIds && communityIds.length > 0 && communityIds.includes(this.residentialForm.controls['community'].value?.id)
        )
      )
    ) {
      this.pmaService.isLeaseRestricted({
        user_matching: {
          email: this.residentialForm.get('email').value,
          user_id: userId,
          unit_id: this.residentialForm.get('insurable_id').value
        }
      }).pipe(
        finalize(() => this.initialLoading = false)
      ).subscribe((result: LeaseRestrictedRes) => {
        if (result.match === true) {
          this.initPolicyApplication();
        }
        else {
          this.residential = null;

          const error = result?.error ?? 'Oops! Something went wrong. We couldn\'t retrieve the information. Please try again later.';
          this.toastr.error(error, null, {
            positionClass: 'toast-bottom-center',
            timeOut: 8000
          });
        }
      });

      return;
    }

    this.initPolicyApplication();
  }

  initPolicyApplication(): void {
    this.toastr.info(this.translate.instant('form.toast_message'), null, {
      positionClass: 'toast-top-center',
    });

    this.initialLoading = true;
    let accountId = null;

    if (this.accountId) {
      accountId = this.accountId;

      if (this.nonpreferredAccountSelection) {
        accountId = this.residentialForm.get('pm_account').value?.id ?? this.accountId;
      }
    }

    const body = {
      agency_id: this.agencyId,
      account_id: accountId,
      policy_type_id: 1,
      policy_insurables_attributes: [
       {id: this.residentialForm.get('insurable_id').value}
      ]
    };

    this.resService.getFormData('residential', body).subscribe((res) => {
      this.initialLoading = false;
      // this.initialLeadDataEntered = true;
      this.residential = res;
      if (this.isQBE) {
        this.questions = res && res.questions && res.questions.length > 0 ? res.questions : [];
        this.setAnswers();
      } else {
        this.questions = [];
      }
      this.adjustFormAfterAddressSelection();
      if (!this.isUpdater && !this.preferred_ho4) {
        if (this.isExpandedCommunityDetails && this.isPmDetailsForm) {
          this.setCommunityDetailsValidators();
        } else {
          this.clearCommunityDetailsValidators();
        }
      }

    }, (err) => {
      this.initialLoading = false;
    });
  }

  getBillingStrategies(agencyId, carrierId?) {
    this.resService.getBillingStrategies(agencyId, carrierId)
      .subscribe((x: any) => {
        this.billingStrategyId = x.map(i => {
          return {
            value: i.title,
            label: 'form.' + i.title.toLowerCase()
          };
        });
        this.billingStrategy = x;
        if (this.residentialForm.controls['billing_strategy_id'] && !this.residentialForm.controls['billing_strategy_id'].value) {
          const defaultBillingStrategy = this.billingStrategy.find(s => s.title === 'Monthly');
          if (defaultBillingStrategy) {
            this.residentialForm.controls['billing_strategy_id'].setValue(defaultBillingStrategy.title);
          }
        }
      });
  }

  addInsured($event?) {
    if ($event) {
      $event.preventDefault();
    }
    const newInsured = new FormGroup({
      spouse: new FormControl(false),
      first_name: new FormControl(null, [Validators.maxLength(30), prohibitedCharactersValidator()]),
      last_name: new FormControl(null, [Validators.maxLength(30), prohibitedCharactersValidator()]),
      middle_name: new FormControl(null, [Validators.maxLength(25), prohibitedCharactersValidator()]),
      contact_phone: new FormControl(null, []),
      birth_date_sub: new FormControl(null, []),
      email: new FormControl(null, [customEmailValidator()])
    });
    const insuredUserArr: FormArray = this.residentialForm.controls['insured_user_arr'] as FormArray;
    insuredUserArr.push(newInsured);
  }

  removeInsured(index) {
    const insuredUserArr: FormArray = this.residentialForm.controls['insured_user_arr'] as FormArray;
    insuredUserArr.removeAt(index);
  }

  private getCoverageLimits(withoutLoading = false) {
    if (this.confirmElem) {
      this.confirmElem.nativeElement.scrollIntoView({ behavior: 'smooth' });
    }
    this.coverageOptionsError = '';
    if (!this.state.id) {
      return;
    }
    this.state.agency_id = this.residential.agency_id;
    const body = this.getNewEstimateBodyByState();
    if (body.billing_strategy_id && body.agency_id) {
      body.estimate_premium = true;
      if (!withoutLoading) {
        this.loadingCoverageOptions = true;
      }
      this.resService.getCoverageOptions(body).pipe(
        finalize(() => this.loadingCoverageOptions = false)
      ).subscribe((res) => {
        if (!withoutLoading) {
          this.coverageOptions = res;
        }
        if (this.isSpecialErrorFlagsCounty) {
          this.getCounties();
        }
        if (!this.isCoverageOptions) {
          this.setFormToNoCoverageOptions(res);
        } else {
          this.initialLeadDataEntered = true;
        }
        this.createResidentialLead('get_covarage_limits');
        this.residentialIconService.setIcons(res);
        this.addLimitForm(res);
        if (res.estimated_installment) {
          this.totalChange.emit(Number(res.estimated_premium));
        }
      }, (err) => {
        const message = err && err.error ? err.error : 'Something went wrong';
        this.toastr.error(message);
      });
    }
  }

  getCounties() {
    this.initialLeadDataEntered = false;
    this.coverageOptionsError = this.translate.instant('residential.this_address_requires_county_selection');
    this.resService.getCounties(this.getInsurableId).subscribe((response: any) => {
      this.counties = response.options;
      this.showCountyField = true;
    }, err => {
      this.coverageOptionsError = err.message;
    });
  }

  setFormToNoCoverageOptions(res) {
    this.initialLeadDataEntered = false;
    this.coverageOptionsError = res && res.estimated_premium_errors && res.estimated_premium_errors[0];
  }

  private getNewEstimateBodyByState() {
    const values = this.residentialForm.getRawValue();

    const body: any = {
      insurable_id: values.insurable_id,
      additional_insured: this.isQBEAndStateMO ? 0 : this.state.number_insured,
      effective_date: moment(this.state.effective_date || moment().add(1, 'days')).format('YYYY-MM-DD'),
      coverage_selections: this.state.coverage_selection,
      billing_strategy_id: this.state.billing_period,
      agency_id: this.state.agency_id,
      estimate_premium: false,
      carrier_id: this.residential.carrier_id
    }
    if (this.accountId) {
      body.account_id = this.accountId;
    }
    return body;
  }

  addLimitForm(res) {
    const coverageOptions = this.applyCustomLimits(res.coverage_options).filter(co => co.requirement !== 'forbidden') || [];
    const fbRes = {};
    coverageOptions.forEach(option => {
      if (option.options_type === 'multiple_choice') {
        if (option.requirement === 'required') {
          fbRes[option.uid] = [option.options[0].value, [Validators.required]];
        } else {
          if (option.options.length === 1) {
            option.options_type = 'optional_checkbox';
          }
          option.options = [{value: false, data_type: 'none'}].concat(option.options);
          fbRes[option.uid] = [false];
        }
      } else {
        if (option.requirement === 'required') {
          if (option.options_type === 'none') {
            fbRes[option.uid] = [{value: true, disabled: true}, [Validators.required]];
          } else {
            fbRes[option.uid] = [true, [Validators.required]];
          }

        } else {
          fbRes[option.uid] = [false];
        }
      }
    });

    this.additionalOptions = this.applyCustomLimits(coverageOptions);
    if (!this.residentialForm.controls['new_carrier']) {
      this.residentialForm.addControl('new_carrier', this.fb.group(fbRes));
      const newCarrier: FormGroup = this.residentialForm.get('new_carrier') as FormGroup;
      if (newCarrier.value) {
        this.calculateNewCoverageLimits(newCarrier.getRawValue());
      }

      /*
       ** If coverageOptions.has_pets equals true
       ** Pet damage checkbox should be checked by default
       */
      if (newCarrier.controls?.pet_damage
        && res.has_pets === true) {
        newCarrier.controls.pet_damage.setValue(true, {emitEvent: false});
      }

      newCarrier.valueChanges
        .pipe(
          distinctUntilChanged(),
          debounceTime(300),
          untilDestroyed(this)
        )
        .subscribe((values) => {
          this.calculateNewCoverageLimits(newCarrier.getRawValue());
          this.createResidentialLead('new_carrier');
        });
      this.isLiabilityEqualHighValue(newCarrier);
      this.newCarrierFormInited.next();
    } else {
      const newCarrierForm: FormGroup = this.residentialForm.controls['new_carrier'] as FormGroup;
      const existedFieldsId = Object.keys(newCarrierForm.controls);
      const serverFieldsId = coverageOptions.map(c => c.uid);
      const newFieldsIds = _.difference(serverFieldsId, existedFieldsId);
      const oldFieldsIds = _.difference(existedFieldsId, serverFieldsId);
      for (const newId of newFieldsIds) {
        newCarrierForm.addControl(String(newId), this.fb.control(fbRes[newId]));
        const newForm = newCarrierForm.controls[newId];
        newForm.setValue(fbRes[newId][0]);
      }
      for (const oldId of oldFieldsIds) {
        newCarrierForm.removeControl(String(oldId));
      }
      newCarrierForm.clearValidators();
      const existingControlIds = Object.keys(newCarrierForm.controls);
      for (const existingId of existingControlIds) {
        const control: FormControl = newCarrierForm.controls[existingId] as FormControl;
        control.clearValidators();
        if (fbRes[existingId] && fbRes[existingId].length > 1) {
          control.setValidators(fbRes[existingId][1]);
        }
        const option = coverageOptions.find(opt => opt.uid === existingId);
        if (option.options_type === 'multiple_choice' && option.requirement === 'required') {
          const isOptionExist = option.options.find(v => v.value === control.value);
          if (!isOptionExist) {
            control.setValue(fbRes[existingId][0]);
          }
        }
      }
    }
  }

  private calculateNewCoverageLimits(values) {
    this.state.coverage_selection = Object.entries(values)
      .filter(([key, value]) => !!value)
      .map(([key, value]) => {
        const option = this.applyCustomLimits(this.coverageOptions.coverage_options).find(opt => opt.uid === key);

        let selectionValue = null;
        if (option.options) {
          if (option.requirement === 'optional' && value === 'N/A') {
            selectionValue = null;
          } else if (typeof value === 'boolean' && option.options.length === 1) {
            /*
             * it's multiple choice but there's only one choice available, that option should be passed as it is,
             * instead of "selection": true
             * it should come like
             * "selection": {
             *   "value": 500000,
             *   "data_type": "currency"
             *  }
             */
            selectionValue = option.options[0];
          } else {
            selectionValue = option.options.find(opt => opt.value === value);
          }
        } else if (option.options_type === 'none') {
          selectionValue = true;
        }

        return {
          uid: key,
          selection: selectionValue,
          category: this.additionalOptions.find(v => v.uid === key).category
        };
      });
    if (values) {
      this.getCoverageLimits(true);
    }
  }

  private isLiabilityEqualHighValue(newCarrier) {
    if (newCarrier && newCarrier.get('liability')) {
      newCarrier.get('liability').valueChanges.subscribe(value => {
        if (this.isQBE && value === 30000000) {
          this.openQuestionsPopup();
        }
      });
    }
  }

  private openQuestionsPopup() {
    const dialog = this.dialog.open(QuestionsPopupComponent, {
      panelClass: 'questions',
      autoFocus: false,
      data: {
        questions: this.questions,
        answers: this.answers
      }
    });
    dialog.afterClosed().subscribe((res) => {
      if (!res) {
        this.f['new_carrier'].get('liability').setValue(10000000);
      }
    });
  }

  private adjustFormAfterAddressSelection() {
    this.getBillingStrategies(this.residential.agency_id, this.residential.carrier_id);
    if (this.residential && this.residential.fields) {
      const residentialNumberOfInsured = this.residential.fields.find(item => item.title === 'Number of Insured') || {};
      for (let i = 0; i < residentialNumberOfInsured; i++) {
        this.addInsured();
      }
    }
    this.state.id = this.residentialForm.get('insurable_id').value;
    this.setResidentialFormAddressAttributes();

    this.adjustNew();
    this.getCoverageLimits();
    this.coverageOptionsInited.emit();
  }

  setResidentialFormAddressAttributes() {
    this.residentialForm.get('city').setValue(this.primaryAddress.city);
    this.residentialForm.get('postal_code').setValue(this.primaryAddress.zip_code);
    this.residentialForm.get('billing_strategy_id').reset();
    this.residentialForm.get('street_name').patchValue(this.primaryAddress.street_number + ' ' + this.primaryAddress.street_name);
    const unit = this.residentialForm.get('unit').value && this.residentialForm.get('unit').value.title ? this.residentialForm.get('unit').value.title :
      this.residentialForm.get('unit').value ? this.residentialForm.get('unit').value : '';
    this.residentialForm.get('street_two').patchValue(unit);
    if (this.nonpreferredAddressSelection) {
      this.residentialForm.get('state').patchValue(this.primaryAddress.state);
    }
  }

  private adjustNew() {
    // this.residentialForm.addControl('installment_day', this.fb.control(1, [Validators.required]));
    this.maxDate = moment(moment.now()).add(59, 'day').toDate();

    if (!this.adjustedNew) {
      const effectiveDate = this.residentialForm.get('effective_date');
      effectiveDate.valueChanges
        .pipe(
          distinctUntilChanged(),
          untilDestroyed(this)
        )
        .subscribe(res => {
          this.state.effective_date = res;
          if (res) {
            this.getCoverageLimits();
          }
        });
      this.adjustedNew = true;
    }
  }

  updateDate($event, d) {
    const expDate = this.residentialForm.get('expiration_date');
    const plusYear = moment(d.value).add(1, 'year').toDate();
    expDate.setValue(plusYear);
    if ($event === 'change') {
      this.createResidentialLead('effective_date');
    }
  }

  checkSpouse(item, i) {
    const insuredUserArr: FormArray = this.residentialForm.controls['insured_user_arr'] as FormArray;
    if (item) {
      insuredUserArr.controls
        .filter((value, index) => index !== i)
        .map((value) => {
          const form = value as FormGroup;
          const spouseControl = form.controls['spouse'];
          if (spouseControl.value === true) {
            spouseControl.setValue(false, {emitEvent: false});
          }
        });
    }
  }

  get isEffectiveDateEqualToday() {
    return moment().isSame(moment(this.residentialForm.get('effective_date').value), 'day');
  }

  submitForm() {
    this.dataLayerResidentialService.formSubmitOne();
    this.submitted = true;

    if (this.isEffectiveDateEqualToday && !this.controlConfirmToday.value) {
      this.confirmElem.nativeElement.scrollIntoView({ behavior: 'smooth' });
      this.toastr.error(this.translate.instant('form.you_cant_proceed_without_agreeing'));
      return;
    }

    if (this.residentialForm.invalid) {
      this.toastr.error(this.translate.instant('form.please_fill_out_all_required_fields'));
      return;
    }

    if (this.isAllTenantsListed && this.controlAllTenantsListed && this.controlAllTenantsListed.value === false) {
      this.toastr.error(this.translate.instant('form.confirm_roommates'));
      return;
    }

    const formValues = this.residentialForm.getRawValue();
    const residential: any = JSON.parse(JSON.stringify(this.residential));

    if (this.accountId) {
      residential.account_id = this.accountId;

      if (this.nonpreferredAccountSelection) {
        residential.account_id = formValues.pm_account?.id;
      }
    }

    residential.billing_strategy_id = this.billingStrategy.find(x => x.title === this.residentialForm.value.billing_strategy_id).id;
    residential.effective_date = moment(formValues.effective_date).format('YYYY-MM-DD');
    residential.expiration_date = moment(formValues.expiration_date).format('YYYY-MM-DD');
    residential.fields[0].value = formValues.number_of_insured;
    residential.policy_insurables_attributes = [{insurable_id: formValues.insurable_id}];
    residential.policy_rates_attributes = [...this.policyRatesAttributes, ...this.checkboxesIds];
    residential.policy_type_id = 1;
    residential.policy_users_attributes = [{
      primary: true,
      spouse: false,
      user_attributes: {
        email: formValues.email,
        profile_attributes: {
          birth_date: moment(formValues.birth_date_main).format('YYYY-MM-DD'),
          first_name: formValues.first_name,
          last_name: formValues.last_name,
          middle_name: formValues.middle_name,
          contact_phone: formValues.contact_phone,
        },
        address_attributes: {
          street_name: formValues.street_name,
          street_two: formValues.street_two,
          city: formValues.city,
          state: formValues.state,
          zip_code: formValues.postal_code
        }
      }
    }];

    const pmaToken = this.pmaService.insurableByAuthTokenStore.getValue();

    if (pmaToken && pmaToken?.policy_user?.id) {
      residential.policy_users_attributes[0].id = pmaToken?.policy_user?.id;
    }

    if (pmaToken && pmaToken?.user?.id) {
      residential.policy_users_attributes[0].user_attributes.id = pmaToken?.user?.id;
    }

    this.addExtraSettings(residential, formValues.community_details);
    residential.extra_settings = {
      ...residential.extra_settings,
      paperless_flag: formValues.paperless_flag
    };

    if (this.nonpreferredAccountSelection && this.residentialForm.get('pm_account').value) {
      const account = (this.residentialForm.get('pm_account').value as PmAccount);
      const address = account.address;

      this.addPmDetails(residential, {
        entity_type: 'company',
        company_name: account.title,
        address: `${address.street_number} ${address.street_name}`,
        address2: '',
        city: address.city,
        state: address.state,
        postal_code: address.zip_code
      });
    } else if ((this.residentialForm.controls.pm_details as FormGroup).controls['address'].validator) {
      this.addPmDetails(residential, formValues.pm_details);
    }

    if (formValues.insured_user_arr && formValues.insured_user_arr.length) {
      const filteredUsers = formValues.insured_user_arr.filter(user => this.isNotEmptyUser(user));
      const users = filteredUsers.map(x => {
        return {
          primary: false,
          spouse: Boolean(x.spouse),
          user_attributes: {
            email: x.email,
            profile_attributes: {
              birth_date: moment(x.birth_date_sub).format('YYYY-MM-DD'),
              first_name: x.first_name,
              last_name: x.last_name,
              middle_name: x.middle_name,
              contact_phone: x.contact_phone,
            }
          },
        };
      });
      residential.policy_users_attributes = [...residential.policy_users_attributes, ...users];
    }

    if (formValues && formValues['new_carrier']) {
      residential.coverage_selections = Object.entries(formValues['new_carrier'])
        // .filter(([key, value]) => !!value)
        .map(([key, value]) => {
          const option = this.applyCustomLimits(this.coverageOptions.coverage_options).find(opt => opt.uid === key);
          let selectionValue = null;
          if (!value) {
            selectionValue = false;
          } else if (option.options_type === 'multiple_choice' && option.requirement === 'optional' && option.options.length === 1) {
            selectionValue = option.options[0];
          } else if (option.options) {
            selectionValue = option.options.find(opt => opt.value === value);
          } else if (option.options_type === 'none' && value) {
            selectionValue = true;
          }

          return {
            uid: key,
            selection: selectionValue,
            category: this.additionalOptions.find(v => v.uid === key).category
          };
        });
    }

    if (!this.isQBE) {
      residential.extra_settings = {
        ...residential.extra_settings,
        installment_day: formValues.installment_day
      };
    }
    const body = this.getNewEstimateBodyByState();
    body.estimate_premium = true;
    this.loader = true;
    this.resService.getCoverageOptions(body).subscribe((res) => {
      if (!res.valid) {
        if (res.estimated_premium_errors) {
          res.estimated_premium_errors.forEach(err => {
            this.toastr.error(err);
          });
        }
        this.loader = false;
      } else {
        this.formSubmit.emit({
          residential,
          additionalInformation: {
            primaryAddress: this.primaryAddress,
            coverageOptions: this.coverageOptions,
            email: formValues.email,
            billingStrategy: this.billingStrategy.find(x => x.title === this.residentialForm.value.billing_strategy_id)
          }
        });
      }
    });
  }

  checkEmailsDifference() {
    const mainEmail = this.residentialForm.controls['email'].value;
    const users = this.residentialForm.controls['insured_user_arr'] as FormArray;
    if (mainEmail && users) {
      this.emailValidateService.checkEmailsDifference(mainEmail, users);
    }
  }

  private addExtraSettings(residential, settings) {
    const settingsContainsNonEmptyValues = Object.values(settings).some(v => !!v);
    const nonEmptySettingsObject =
      Object.entries(settings)
        .filter(([key, value]) => value !== null && value !== undefined && value !== '')
        .reduce((acc, [key, val]) => Object.assign(acc, {[key]: val}), {});
    if (settingsContainsNonEmptyValues) {
      residential.extra_settings = {
        ...residential.extra_settings,
        ...nonEmptySettingsObject
      };
    }
  }

  private addPmDetails(residential, pmDetails) {
    let additionalInterest = {
      entity_type: pmDetails.entity_type,
      email_address: pmDetails.email_address,
      addr1: pmDetails.address,
      addr2: pmDetails.address2,
      city: pmDetails.city,
      state: pmDetails.state,
      zip: pmDetails.postal_code,
      phone_number: pmDetails.phone_number
    };

    if (pmDetails.entity_type === 'company') {
      additionalInterest = Object.assign(additionalInterest, {
        company_name: pmDetails.company_name
      });
    } else {
      additionalInterest = Object.assign(additionalInterest, {
        first_name: pmDetails.first_name,
        last_name: pmDetails.last_name,
      });
    }

    residential.extra_settings = {
      ...residential.extra_settings,
      additional_interest: additionalInterest
    };
  }

  compareInstallmentItems(item1, item2) {
    return parseInt(item1, 10) === parseInt(item2, 10);
  }

  setAnswers() {
    this.questions.forEach(question => question.options = question.options.map(item => String(item)));
    this.questions.forEach(question => {
      this.answers[question.questionId] = question.value;
    });
  }

  displayUnitFn(unit: Unit | string): any {
    if (typeof unit === 'object' && unit && unit.hasOwnProperty('title') && unit['title'] === null) {
      return 'N/A';
    }
    return unit && unit['title'] ? unit['title'] : unit;
  }


  initPMAccountSelector() {
    const pmAccount = this.residentialForm.get('pm_account');
    pmAccount.addValidators(Validators.required);

    pmAccount.valueChanges.pipe(
      takeWhile(() => this.isActive)
    ).subscribe((account: PmAccount) => {
      const address = account?.address;
      if (!isAddressFilled(address)) {
        return;
      }
      this.residentialForm.get('pm_details').patchValue({
        entity_type: 'company',
        company_name: account.title,
        address: `${address.street_number} ${address.street_name}`,
        address2: [''],
        city: address.city,
        state: address.state,
        postal_code: address.zip_code
      });
    });
  }

  showPaperlessAgreement(event: Event): void {
    event.preventDefault();
    this.dialog.open(PaperlessAgreementComponent, {
      width: '850px',
    });
  }

  get f() {
    return this.residentialForm.controls;
  }

  get isQBE() {
    return this.residential && this.residential.carrier_id && this.residential.carrier_id === 1;
  }

  get isQBEAndStateMO() {
    return this.isQBE && this.primaryAddress?.state === 'MO';
  }

  get isStateWAorFL() {
    return (this.primaryAddress?.state === 'WA' || this.primaryAddress?.state === 'FL');
  }

  get isExpandedCommunityDetails() {
    return this.isQBE && this.isStateWAorFL;
  }

  get isCountyValid() {
    return !this.residentialForm.get('county').value && this.residentialForm.controls['address']?.touched;
  }

  get isSpecialErrorFlagsCounty() {
    return this.coverageOptions.special_error_flags &&
      this.coverageOptions.special_error_flags.length > 0 &&
      this.coverageOptions.special_error_flags.includes('county_resolution_failure');
  }

  get isCoverageOptions() {
    return this.coverageOptions &&
      this.coverageOptions.coverage_options &&
      Object.keys(this.coverageOptions.coverage_options).length > 0;
  }

  private applyCustomLimits(coverageOptions) {
    return coverageOptions.map(item => {
      return {
        ...item,
        options: !CUSTOM_LIMITS[item.uid] ? item.options : item.options.filter(opt => {
          if (CUSTOM_LIMITS[item.uid].agencies[this.agencyId] || CUSTOM_LIMITS[item.uid].accounts[this.accountId]) {
            if ((opt.value / 100) >= CUSTOM_LIMITS[item.uid].agencies[this.agencyId]) {
              return opt;
            }
            if (opt.value / 100 >= CUSTOM_LIMITS[item.uid].accounts[this.accountId]) {
              return opt;
            }
          } else {
            return opt;
          }
        })
      };
    });
  }

  get isUpdater() {
    return this.agencyId === 313;
  }

  get isAllTenantsListed() {
    const brandingProfileAttributes = this.brandingService.getBrandingInfoSnapshot().profile_attributes;
    const isAllTenantsListed = brandingProfileAttributes.some(item => item.name === 'all_tenants_listed' && item.value === 'true')
    return isAllTenantsListed;
  }

  get isPmDetailsForm() {
    return !this.nonpreferredAccountSelection && this.preferred_ho4 === false
  }

  private sortUnits(units) {
    if (units && units.length) {
      const sortedLetters = units.sort((a, b) => (a.title > b.title) ? 1 : ((b.title > a.title) ? -1 : 0));
      const sortedNumbers = sortedLetters.sort((a, b) => (a.title != null ? a.title : Infinity) - (b.title != null ? b.title : Infinity));
      const unitWithNATitle = sortedNumbers.find(unit => unit.title === 'N/A' || !unit.title);
      if (unitWithNATitle) {
        return [unitWithNATitle, ...sortedNumbers.filter(item => item.title !== 'N/A' && item.title)];
      } else {
        return sortedNumbers;
      }
    } else {
      return [];
    }
  }

  get getInsurableId() {
    return this.residentialForm.get('insurable_id') && this.residentialForm.get('insurable_id').value ? this.residentialForm.get('insurable_id').value : null;
  }
}
